import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import { Controller, ControllerRenderProps } from "react-hook-form";
import {
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useState, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { useMsal } from "@azure/msal-react";
import { DataTableShell } from "../../components/table/DataTableShell";
import { EntityFormDialog } from "./components/EntityFormDialog";
import { vendorSchema, VendorFormData } from "./schemas/vendorSchema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VendorRow = {
  _id: string;
  name: string;
  nameLower: string;
  defaultGlNumber?: string;
  defaultCard?: string;
  type: "mapped" | "ocr-exception" | "review-queue";
  glAccountDescription: string;
  notes?: string;
  isActive: boolean;
  createdAt: number;
  deletedAt?: number;
};

type GlAccountRow = {
  _id: string;
  number: string;
  description: string;
  accountType?: string;
};

type CardRow = {
  _id: string;
  number: string;
  description?: string;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function VendorsAdminPage() {
  // --- MSAL identity (verbatim from ReviewQueue.tsx lines 133-158) ---
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";

  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const isAdmin = userRecord?.role === "admin";

  // --- Data queries (D-33 MANDATORY: useStableQuery for all data) ---
  const vendorsRaw = useStableQuery(api.vendors.listActive, {}, []);
  const usageCountsRaw = useStableQuery(api.vendors.usageCounts, {}, {});
  const glAccountsRaw = useStableQuery(api.glAccounts.listActive, {}, []);
  const cardsRaw = useStableQuery(api.cards.listActive, {}, []);
  const pendingReviewCountRaw = useStableQuery(api.vendors.countPendingReview, {}, 0);

  const loading = vendorsRaw === undefined;
  const vendors: VendorRow[] = (vendorsRaw ?? []) as VendorRow[];
  const usageCounts: Record<string, number> = (usageCountsRaw ?? {}) as Record<string, number>;
  const glAccounts: GlAccountRow[] = (glAccountsRaw ?? []) as GlAccountRow[];
  const cards: CardRow[] = (cardsRaw ?? []) as CardRow[];
  const pendingReviewCount: number = (pendingReviewCountRaw ?? 0) as number;

  // --- Mutations ---
  const vendorCreate = useMutation(api.vendors.create);
  const vendorUpdate = useMutation(api.vendors.update);
  const vendorSoftDelete = useMutation(api.vendors.softDelete);
  const rerunReviewQueue = useMutation(api.vendors.rerunReviewQueue);

  // --- Filter state ---
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCards, setSelectedCards] = useState<CardRow[]>([]);
  const [typeFilter, setTypeFilter] = useState<
    "all" | "mapped" | "ocr-exception" | "review-queue"
  >("all");

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedCards([]);
    setTypeFilter("all");
  }, []);

  const hasFilters = searchTerm.trim() !== "" || selectedCards.length > 0 || typeFilter !== "all";

  // --- Client-side filtered rows ---
  const filteredVendors = useMemo(() => {
    let rows = vendors;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (v) => v.nameLower.includes(q) || (v.notes?.toLowerCase().includes(q) ?? false),
      );
    }
    if (selectedCards.length > 0) {
      const selectedNumbers = selectedCards.map((c) => c.number);
      rows = rows.filter((v) => v.defaultCard && selectedNumbers.includes(v.defaultCard));
    }
    if (typeFilter !== "all") rows = rows.filter((v) => v.type === typeFilter);
    return rows;
  }, [vendors, searchTerm, selectedCards, typeFilter]);

  // --- Dialog state ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<VendorRow | null>(null);
  const [mutationError, setMutationError] = useState<string | undefined>(undefined);
  // Track selected type to conditionally disable GL/card fields within renderFields closure
  const [formType, setFormType] = useState<"mapped" | "ocr-exception" | "review-queue">("mapped");

  // Delete confirm state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VendorRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Re-evaluate Review Queue confirm state
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunResult, setRerunResult] = useState<{
    scanned: number;
    promotedToCategorized: number;
    updatedReviewQueue: number;
    updatedOcrException: number;
    updatedVendorUnknown: number;
    unchanged: number;
    stillPendingReview: number;
  } | null>(null);

  // --- Snackbar state ---
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState<"success" | "error">("success");

  const showSnack = useCallback((message: string, severity: "success" | "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  }, []);

  // --- Dialog open handlers ---
  const openAdd = useCallback(() => {
    setEditingRow(null);
    setMutationError(undefined);
    setFormType("mapped");
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((row: VendorRow) => {
    setEditingRow(row);
    setMutationError(undefined);
    setFormType(row.type);
    setDialogOpen(true);
  }, []);

  const openDeleteConfirm = useCallback((row: VendorRow) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingRow(null);
    setMutationError(undefined);
  }, []);

  // --- Submit handler ---
  const handleSubmit = useCallback(
    async (data: VendorFormData) => {
      try {
        if (editingRow) {
          await vendorUpdate({
            id: editingRow._id as Id<"vendors">,
            name: data.name,
            type: data.type,
            defaultGlNumber: data.type === "mapped" ? (data.defaultGlNumber || undefined) : undefined,
            defaultCard: data.type === "mapped" ? (data.defaultCard || undefined) : undefined,
            glAccountDescription: data.glAccountDescription,
            notes: data.notes || undefined,
            reviewerEmail: userRecord?.email ?? email,
          });
          showSnack("Vendor saved", "success");
        } else {
          await vendorCreate({
            name: data.name,
            type: data.type,
            defaultGlNumber: data.type === "mapped" ? (data.defaultGlNumber || undefined) : undefined,
            defaultCard: data.type === "mapped" ? (data.defaultCard || undefined) : undefined,
            glAccountDescription: data.glAccountDescription,
            notes: data.notes || undefined,
            reviewerEmail: userRecord?.email ?? email,
          });
          showSnack("Vendor added", "success");
        }
        setDialogOpen(false);
        setEditingRow(null);
        setMutationError(undefined);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to save vendor. Please try again.";
        setMutationError(message);
        throw err; // re-throw so EntityFormDialog's isSubmitting resets
      }
    },
    [editingRow, email, vendorCreate, vendorUpdate, showSnack],
  );

  // --- Re-evaluate Review Queue handler ---
  const handleConfirmRerun = useCallback(async () => {
    setRerunning(true);
    try {
      const result = await rerunReviewQueue({
        reviewerEmail: userRecord?.email ?? email,
      });
      setRerunResult(result);
      showSnack(
        `Re-evaluated ${result.scanned} rows: ${result.promotedToCategorized} promoted to categorized, ${result.stillPendingReview} still in Review Queue`,
        "success",
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to re-evaluate Review Queue.";
      showSnack(message, "error");
    } finally {
      setRerunning(false);
    }
  }, [rerunReviewQueue, email, userRecord, showSnack]);

  const handleCloseRerun = useCallback(() => {
    setRerunOpen(false);
    setRerunResult(null);
  }, []);

  // --- Delete handler ---
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await vendorSoftDelete({
        id: deleteTarget._id as Id<"vendors">,
        reviewerEmail: userRecord?.email ?? email,
      });
      setDeleteOpen(false);
      setDeleteTarget(null);
      showSnack("Vendor deleted", "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to delete vendor.";
      showSnack(message, "error");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, email, vendorSoftDelete, showSnack]);

  // --- Default values for dialog ---
  const dialogDefaultValues: VendorFormData = useMemo(() => {
    if (editingRow) {
      return {
        name: editingRow.name,
        type: editingRow.type,
        defaultGlNumber: editingRow.defaultGlNumber ?? "",
        defaultCard: editingRow.defaultCard ?? "",
        glAccountDescription: editingRow.glAccountDescription ?? "",
        notes: editingRow.notes ?? "",
      };
    }
    return {
      name: "",
      type: "mapped" as const,
      defaultGlNumber: "",
      defaultCard: "",
      glAccountDescription: "",
      notes: "",
    };
  }, [editingRow]);

  // --- Columns ---
  const COLUMNS: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Vendor Name",
        flex: 2,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" noWrap title={params.value as string}>
            {params.value as string}
          </Typography>
        ),
      },
      {
        field: "defaultGlNumber",
        headerName: "Default GL",
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            size="small"
            label={(params.value as string) ?? "—"}
            variant="outlined"
          />
        ),
      },
      {
        field: "defaultCard",
        headerName: "Default Card",
        width: 110,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            size="small"
            label={(params.value as string) ?? "—"}
            variant="outlined"
          />
        ),
      },
      {
        field: "type",
        headerName: "Type",
        width: 130,
        renderCell: (params: GridRenderCellParams) => {
          const t = params.value as "mapped" | "ocr-exception" | "review-queue";
          const color =
            t === "mapped" ? "success" : t === "ocr-exception" ? "warning" : "error";
          return <Chip label={t} size="small" color={color} variant="outlined" />;
        },
      },
      {
        field: "glAccountDescription",
        headerName: "GL Description",
        width: 220,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={(params.value as string) ?? ""}>
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.value as string}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "usageCount",
        headerName: "Usage",
        width: 90,
        renderCell: (params: GridRenderCellParams) => {
          if (usageCountsRaw === undefined) {
            return <Skeleton width={40} height={16} />;
          }
          const count = usageCounts[params.row.name as string] ?? 0;
          return <Typography variant="body2">{count}</Typography>;
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            noWrap
            title={(params.value as string) ?? ""}
            sx={{ color: "text.secondary" }}
          >
            {(params.value as string) ?? ""}
          </Typography>
        ),
      },
      {
        field: "_id",
        headerName: "",
        width: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              aria-label={`Edit vendor ${params.row.name as string}`}
              onClick={() => openEdit(params.row as VendorRow)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Delete vendor ${params.row.name as string}`}
              onClick={() => openDeleteConfirm(params.row as VendorRow)}
              sx={{ color: "error.main" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [openEdit, openDeleteConfirm, usageCountsRaw, usageCounts],
  );

  // --- Filter bar ---
  const filterBar = (
    <>
      <TextField
        size="small"
        placeholder="Search vendors..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />,
        }}
        sx={{ minWidth: 220 }}
      />
      <Autocomplete
        multiple
        size="small"
        options={cards}
        getOptionLabel={(card: CardRow) => `${card.number} — ${card.description ?? card.number}`}
        value={selectedCards}
        onChange={(_e, v) => setSelectedCards(v as CardRow[])}
        renderTags={(tagValue, getTagProps) =>
          (tagValue as CardRow[]).map((card, index) => (
            <Chip
              size="small"
              label={card.number}
              variant="outlined"
              {...getTagProps({ index })}
              key={card._id}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Card" sx={{ minWidth: 180 }} />
        )}
        sx={{ minWidth: 180 }}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        value={typeFilter}
        onChange={(_e, v) =>
          v && setTypeFilter(v as "all" | "mapped" | "ocr-exception" | "review-queue")
        }
        aria-label="Vendor type filter"
      >
        <ToggleButton value="all" aria-label="All vendors">
          All
        </ToggleButton>
        <ToggleButton value="mapped" aria-label="Mapped vendors">
          Mapped
        </ToggleButton>
        <ToggleButton value="ocr-exception" aria-label="OCR-exception vendors">
          OCR-exception
        </ToggleButton>
        <ToggleButton value="review-queue" aria-label="Review-queue vendors">
          Review-queue
        </ToggleButton>
      </ToggleButtonGroup>
      {hasFilters && (
        <Button variant="outlined" size="small" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </>
  );

  // --- Role guard ---
  if (userRecord === undefined) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You don&apos;t have access to the Vendors admin page.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Source-of-truth banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Vendor catalog source-of-truth: <code>docs/vendor-end-glaccont.md</code>. Edits here are
        runtime overrides until the next migration sync.
      </Alert>

      {/* Page header action bar */}
      <Stack data-tour="vendors-actions" direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
        <Tooltip
          title={
            pendingReviewCount === 0
              ? "Review Queue is empty"
              : "Re-apply the current vendor catalog to every row currently in Review Queue. Mapped vendors get promoted to categorized; review-queue / ocr-exception vendors get their reason refreshed."
          }
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setRerunOpen(true)}
              disabled={pendingReviewCount === 0 || rerunning}
              aria-label="Re-evaluate Review Queue"
            >
              {rerunning ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Re-evaluate Review Queue ({pendingReviewCount})
            </Button>
          </span>
        </Tooltip>
        <Button variant="contained" onClick={openAdd} aria-label="Add Vendor">
          Add Vendor
        </Button>
      </Stack>

      <DataTableShell
        data-tour="page-vendors"
        title="Vendors"
        subtitle="Manage vendor-to-GL mappings and default card assignments"
        rows={filteredVendors as any}
        columns={COLUMNS}
        loading={loading}
        totalBeforeFilters={vendors.length}
        filterBar={filterBar}
        onResetFilters={hasFilters ? clearFilters : undefined}
        emptyTitle="No vendors found"
        emptyDescription="Add a vendor to start building the categorization mapping."
        itemLabel="vendor"
        gridProps={{
          initialState: {
            sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          },
          pageSizeOptions: [10, 25, 50, 100],
          disableRowSelectionOnClick: true,
          autoHeight: true,
        }}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <EntityFormDialog<VendorFormData>
        title={editingRow ? "Edit Vendor" : "Add Vendor"}
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        schema={vendorSchema}
        defaultValues={dialogDefaultValues}
        submitLabel={editingRow ? "Save Changes" : "Add Vendor"}
        auditHref={editingRow ? `/audit?entity=vendor&id=${editingRow._id}` : undefined}
        errorAlert={mutationError}
        renderFields={(control, errors) => (
          <>
            {/* Field 1: Vendor Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }: { field: ControllerRenderProps<VendorFormData, "name"> }) => (
                <TextField
                  {...field}
                  label="Vendor Name"
                  fullWidth
                  size="small"
                  autoFocus={!editingRow}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            {/* Field 2: Type */}
            <Controller
              name="type"
              control={control}
              render={({ field }: { field: ControllerRenderProps<VendorFormData, "type"> }) => (
                <FormControl fullWidth size="small" error={!!errors.type}>
                  <InputLabel id="vendor-type-label">Type</InputLabel>
                  <Select
                    labelId="vendor-type-label"
                    label="Type"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setFormType(e.target.value as "mapped" | "ocr-exception" | "review-queue");
                    }}
                  >
                    <MenuItem value="mapped">Mapped</MenuItem>
                    <MenuItem value="ocr-exception">OCR-exception</MenuItem>
                    <MenuItem value="review-queue">Review-queue</MenuItem>
                  </Select>
                  <FormHelperText>
                    {errors.type?.message ??
                      "mapped: fixed GL + card. ocr-exception: GL from invoice OCR. review-queue: always manual."}
                  </FormHelperText>
                </FormControl>
              )}
            />

            {/* Field 3: Default GL Account */}
            <Controller
              name="defaultGlNumber"
              control={control}
              render={({ field }: { field: ControllerRenderProps<VendorFormData, "defaultGlNumber"> }) => {
                const selectedGl = glAccounts.find((gl) => gl.number === field.value) ?? null;
                return (
                  <Autocomplete
                    size="small"
                    disabled={formType !== "mapped"}
                    options={glAccounts}
                    getOptionLabel={(gl: GlAccountRow) => `${gl.number} — ${gl.description}`}
                    value={selectedGl}
                    onChange={(_e, v) => field.onChange(v ? (v as GlAccountRow).number : "")}
                    isOptionEqualToValue={(a: GlAccountRow, b: GlAccountRow) => a.number === b.number}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Default GL Account"
                        size="small"
                        error={!!errors.defaultGlNumber}
                        helperText={errors.defaultGlNumber?.message}
                      />
                    )}
                  />
                );
              }}
            />

            {/* Field 4: Default Card */}
            <Controller
              name="defaultCard"
              control={control}
              render={({ field }: { field: ControllerRenderProps<VendorFormData, "defaultCard"> }) => {
                const selectedCard = cards.find((c) => c.number === field.value) ?? null;
                return (
                  <Autocomplete
                    size="small"
                    disabled={formType !== "mapped"}
                    options={cards}
                    getOptionLabel={(card: CardRow) => `${card.number} — ${card.description ?? card.number}`}
                    value={selectedCard}
                    onChange={(_e, v) => field.onChange(v ? (v as CardRow).number : "")}
                    isOptionEqualToValue={(a: CardRow, b: CardRow) => a.number === b.number}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Default Card"
                        size="small"
                        error={!!errors.defaultCard}
                        helperText={errors.defaultCard?.message}
                      />
                    )}
                  />
                );
              }}
            />

            {/* Field 5: GL Account Description */}
            <Controller
              name="glAccountDescription"
              control={control}
              render={({ field }: { field: ControllerRenderProps<VendorFormData, "glAccountDescription"> }) => (
                <TextField
                  {...field}
                  label="GL Description"
                  multiline
                  minRows={3}
                  maxRows={6}
                  fullWidth
                  size="small"
                  error={!!errors.glAccountDescription}
                  helperText={
                    errors.glAccountDescription?.message ??
                    "Verbatim per-vendor description (instructional for ocr-exception/review-queue)."
                  }
                />
              )}
            />

            {/* Field 6: Notes */}
            <Controller
              name="notes"
              control={control}
              render={({ field }: { field: ControllerRenderProps<VendorFormData, "notes"> }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />
          </>
        )}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Soft-Delete confirm dialog (D-07 verbatim body text)                */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={deleteOpen}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">Delete Vendor?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Soft-delete vendor &apos;{deleteTarget?.name}&apos;? It will hide from dropdowns and
            Review queue; you can restore from the audit trail.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={handleConfirmDelete}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Re-evaluate Review Queue confirm dialog                              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={rerunOpen}
        maxWidth="sm"
        fullWidth
        aria-labelledby="rerun-dialog-title"
      >
        <DialogTitle id="rerun-dialog-title">Re-evaluate Review Queue?</DialogTitle>
        <DialogContent>
          {!rerunResult ? (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                This re-applies the current vendor catalog to{" "}
                <strong>{pendingReviewCount}</strong> transactions in{" "}
                <code>pending_review</code>:
              </Typography>
              <ul style={{ marginTop: 0, paddingLeft: "1.5em" }}>
                <li>
                  Rows whose vendor is now in the catalog as <strong>mapped</strong>{" "}
                  are promoted to <code>categorized</code> with the catalog&apos;s
                  default GL and Card.
                </li>
                <li>
                  Rows matching <strong>review-queue</strong> or{" "}
                  <strong>ocr-exception</strong> vendors get their review reason
                  refreshed to the current catalog verdict.
                </li>
                <li>
                  Rows whose vendor is not in the catalog stay in Review Queue
                  with reason <code>vendor_unknown</code>.
                </li>
              </ul>
              <Alert severity="info" variant="outlined">
                Exact-match only (no fuzzy lookup, no LLM, no OCR). If a vendor
                isn&apos;t matching, add it to the catalog and re-run.
              </Alert>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2">
                Re-evaluation complete. Scanned{" "}
                <strong>{rerunResult.scanned}</strong> rows:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <li>
                  <strong>{rerunResult.promotedToCategorized}</strong> promoted to{" "}
                  <code>categorized</code>
                </li>
                <li>
                  <strong>{rerunResult.updatedReviewQueue}</strong> updated to{" "}
                  <code>review_queue_policy</code>
                </li>
                <li>
                  <strong>{rerunResult.updatedOcrException}</strong> updated to{" "}
                  <code>ocr_exception</code>
                </li>
                <li>
                  <strong>{rerunResult.updatedVendorUnknown}</strong> updated to{" "}
                  <code>vendor_unknown</code>
                </li>
                <li>
                  <strong>{rerunResult.unchanged}</strong> already in sync (no
                  change)
                </li>
                <li>
                  <strong>{rerunResult.stillPendingReview}</strong> remain in
                  Review Queue
                </li>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          {!rerunResult ? (
            <>
              <Button onClick={() => setRerunOpen(false)} disabled={rerunning}>
                Cancel
              </Button>
              <Button
                color="warning"
                variant="contained"
                disabled={rerunning || pendingReviewCount === 0}
                onClick={handleConfirmRerun}
                startIcon={
                  rerunning ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {rerunning ? "Re-evaluating..." : "Re-evaluate Now"}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleCloseRerun}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar (D-08: 4s auto-hide, bottom-center)                        */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          severity={snackSeverity}
          onClose={() => setSnackOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default VendorsAdminPage;
